import { useState, useRef } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useGetBoMeQuery } from "../../store/services/api";

const AGENT_URL = process.env.REACT_APP_AGENT_WORKER_URL;
const AGENT_API_KEY = process.env.REACT_APP_AGENT_WORKER_API_KEY;

interface LearnedEntry {
  id: string;
  term: string;
  meaning: string;
}

interface AgentResponseData {
  sql: string;
  results: Record<string, unknown>[];
  analysis: string;
  /** Id de la interacción persistida por el worker; sin esto no se puede votar. */
  logId?: string;
  /** Presente cuando el turno fue una corrección de vocabulario, no una pregunta. */
  learned?: LearnedEntry;
}

interface Message {
  role: "user" | "agent";
  content: string;
  data?: AgentResponseData;
}

interface HistoryMessage {
  role: "user" | "agent";
  content: string;
  sql?: string;
}

const MAX_HISTORY_MESSAGES = 20;

const buildHistory = (messages: Message[]): HistoryMessage[] =>
  messages.slice(-MAX_HISTORY_MESSAGES).map((msg) => ({
    role: msg.role,
    content: msg.content,
    ...(msg.data?.sql ? { sql: msg.data.sql } : {}),
  }));

const exportToCsv = (results: Record<string, unknown>[]) => {
  if (results.length === 0) return;
  const columns = Object.keys(results[0]);
  const escape = (v: unknown) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [columns.join(","), ...results.map((row) => columns.map((c) => escape(row[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `resultados_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Voto sobre una respuesta. Es la señal que después alimenta la biblioteca de ejemplos
 * verificados del agente: sin etiquetas, lo que se guarda es ruido.
 */
const AgentFeedback = ({ logId, actor }: { logId: string; actor?: string }) => {
  const [vote, setVote] = useState<boolean | null>(null);
  const [correctedSql, setCorrectedSql] = useState("");
  const [correctionSent, setCorrectionSent] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async (isHelpful: boolean, sql?: string) => {
    setSending(true);
    try {
      const res = await fetch(`${AGENT_URL}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${AGENT_API_KEY ?? ""}`,
        },
        body: JSON.stringify({ logId, isHelpful, actor, ...(sql ? { correctedSql: sql } : {}) }),
      });
      // El worker explica en el body qué pasó; mostrar sólo el código dejaba al usuario
      // frente a un "Error 422" sin ninguna pista de qué hacer.
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? body?.details ?? `Error ${res.status}`);
      }
      setVote(isHelpful);
      if (sql) setCorrectionSent(true);
    } catch {
      // El feedback es accesorio: si falla, no se interrumpe la conversación.
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-2 pt-2 border-top-1 surface-border">
      <div className="flex align-items-center gap-2">
        <span className="text-sm text-color-secondary">¿Te sirvió esta respuesta?</span>
        <Button
          icon="pi pi-thumbs-up"
          tooltip="Sí"
          size="small"
          text={vote !== true}
          severity="success"
          disabled={sending}
          onClick={() => send(true)}
          style={{ padding: "2px 6px" }}
        />
        <Button
          icon="pi pi-thumbs-down"
          tooltip="No"
          size="small"
          text={vote !== false}
          severity="danger"
          disabled={sending}
          onClick={() => send(false)}
          style={{ padding: "2px 6px" }}
        />
        {vote !== null && <span className="text-sm text-color-secondary">¡Gracias!</span>}
      </div>

      {vote === false && !correctionSent && (
        <div className="mt-2">
          <label className="block text-sm text-color-secondary mb-1">
            ¿Cuál era el SQL correcto? (opcional — es lo que le enseña al agente)
          </label>
          <InputTextarea
            value={correctedSql}
            onChange={(e) => setCorrectedSql(e.target.value)}
            rows={3}
            className="w-full"
            style={{ fontSize: "0.78rem", fontFamily: "monospace" }}
            placeholder="SELECT …"
          />
          <Button
            label="Enviar corrección"
            size="small"
            className="mt-1"
            disabled={!correctedSql.trim() || sending}
            onClick={() => send(false, correctedSql.trim())}
          />
        </div>
      )}
      {correctionSent && (
        <p className="m-0 mt-1 text-sm text-color-secondary">Corrección guardada. ¡Gracias!</p>
      )}
    </div>
  );
};

/**
 * Respuesta a una corrección de vocabulario. Se aplica de inmediato —el que corrige es un
 * super admin— pero queda visible y deshacible acá mismo, porque a partir de ahora entra en
 * el prompt de todas las consultas.
 */
const LearnedMessage = ({ entry }: { entry: LearnedEntry }) => {
  const [undone, setUndone] = useState(false);
  const [working, setWorking] = useState(false);

  const undo = async () => {
    setWorking(true);
    try {
      const res = await fetch(`${AGENT_URL}/glossary/${entry.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${AGENT_API_KEY ?? ""}`,
        },
        body: JSON.stringify({ active: false }),
      });
      if (res.ok) setUndone(true);
    } catch {
      // Sin efecto: la entrada sigue activa y se puede desactivar desde Ejemplos.
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="surface-100 border-round p-3 text-color" style={{ maxWidth: "80%" }}>
      <div className="flex align-items-start gap-2">
        <i className="pi pi-bookmark text-primary mt-1" style={{ fontSize: "1rem" }} />
        <div>
          <p className="m-0 line-height-3">
            {undone ? (
              <>
                Descartado. <strong>{entry.term}</strong> vuelve a interpretarse como antes.
              </>
            ) : (
              <>
                Anotado: de ahora en más <strong>{entry.term}</strong> significa {entry.meaning}
              </>
            )}
          </p>
          {!undone && (
            <div className="flex align-items-center gap-2 mt-2">
              <Button
                label="Deshacer"
                icon="pi pi-undo"
                size="small"
                text
                severity="secondary"
                disabled={working}
                onClick={undo}
                style={{ padding: "2px 6px" }}
              />
              <span className="text-sm text-color-secondary">
                Se aplica a todas las consultas siguientes
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AgentMessage = ({ data, actor }: { data: AgentResponseData; actor?: string }) => {
  const [showSql, setShowSql] = useState(false);
  const columns = data.results.length > 0 ? Object.keys(data.results[0]) : [];

  return (
    <div className="surface-100 border-round p-3 text-color" style={{ maxWidth: "80%", minWidth: "320px" }}>
      {/* Analysis */}
      <div className="flex align-items-start gap-2 mb-3">
        <i className="pi pi-comment text-primary mt-1" style={{ fontSize: "1rem" }} />
        <p className="m-0 line-height-3">{data.analysis}</p>
      </div>

      {/* Results table */}
      {data.results.length > 0 && (
        <div className="mb-3">
          <div className="flex align-items-center justify-content-between mb-2">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-table text-primary" style={{ fontSize: "0.875rem" }} />
              <span className="text-sm font-medium text-color-secondary">
                {data.results.length} resultado{data.results.length !== 1 ? "s" : ""}
              </span>
            </div>
            <Button
              icon="pi pi-download"
              tooltip="Exportar CSV"
              tooltipOptions={{ position: "left" }}
              size="small"
              text
              severity="secondary"
              onClick={() => exportToCsv(data.results)}
              style={{ padding: "2px 6px" }}
            />
          </div>
          <div className="overflow-x-auto border-round border-1 surface-border">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr className="surface-200">
                  {columns.map((col) => (
                    <th
                      key={col}
                      className="text-left text-color-secondary font-medium"
                      style={{ padding: "8px 12px", borderBottom: "1px solid var(--surface-border)", whiteSpace: "nowrap" }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.results.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? "surface-0" : "surface-50"}>
                    {columns.map((col) => (
                      <td
                        key={col}
                        className="text-color"
                        style={{ padding: "7px 12px", borderBottom: "1px solid var(--surface-border)" }}
                      >
                        {String(row[col] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SQL collapsible */}
      <button
        onClick={() => setShowSql((v) => !v)}
        className="flex align-items-center gap-1 text-sm text-color-secondary cursor-pointer border-none bg-transparent p-0"
        style={{ outline: "none" }}
      >
        <i className={`pi ${showSql ? "pi-chevron-up" : "pi-chevron-down"}`} style={{ fontSize: "0.75rem" }} />
        <i className="pi pi-code" style={{ fontSize: "0.75rem" }} />
        <span>Ver SQL</span>
      </button>
      {showSql && (
        <pre
          className="mt-2 p-2 border-round surface-200 text-color overflow-x-auto m-0"
          style={{ fontSize: "0.78rem", lineHeight: "1.5" }}
        >
          {data.sql}
        </pre>
      )}

      {data.logId && <AgentFeedback logId={data.logId} actor={actor} />}
    </div>
  );
};

export const AgentQueries = () => {
  // MT-011 — El agente de consultas de IA es exclusivo de SUPER_ADMIN por ahora.
  const { data: me, isLoading: meLoading } = useGetBoMeQuery();

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  // Agrupa los turnos de una misma conversación en el log del agente.
  const [conversationId, setConversationId] = useState(() => crypto.randomUUID());
  const bottomRef = useRef<HTMLDivElement>(null);

  // Id del usuario de BO, no su mail: alcanza para atribuir y no guarda datos personales.
  const actor = me?.userId !== undefined ? String(me.userId) : undefined;

  const startNewConversation = () => {
    setMessages([]);
    setConversationId(crypto.randomUUID());
  };

  const handleSend = async () => {
    const trimmed = query.trim();
    if (!trimmed || loading) return;

    const history = buildHistory(messages);
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setQuery("");
    setLoading(true);

    try {
      const res = await fetch(`${AGENT_URL}/query`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${AGENT_API_KEY ?? ""}`,
        },
        body: JSON.stringify({ query: trimmed, history, conversationId, actor }),
      });

      // El worker explica en el body qué pasó; mostrar sólo el código dejaba al usuario
      // frente a un "Error 422" sin ninguna pista de qué hacer.
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? body?.details ?? `Error ${res.status}`);
      }

      const data = await res.json();

      if (data?.learned) {
        setMessages((prev) => [
          ...prev,
          { role: "agent", content: data.analysis, data: data as AgentResponseData },
        ]);
      } else if (data?.sql && data?.analysis) {
        setMessages((prev) => [
          ...prev,
          { role: "agent", content: data.analysis, data: data as AgentResponseData },
        ]);
      } else if (data?.analysis) {
        // Sin SQL: el agente no podía responder con datos y explica qué sí puede hacer.
        setMessages((prev) => [...prev, { role: "agent", content: data.analysis }]);
      } else {
        const answer: string =
          typeof data === "string"
            ? data
            : data.answer ?? data.result ?? data.response ?? JSON.stringify(data);
        setMessages((prev) => [...prev, { role: "agent", content: answer }]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error desconocido";
      setMessages((prev) => [
        ...prev,
        { role: "agent", content: `Error al procesar la consulta: ${message}` },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (meLoading) return <div className="p-4">Cargando…</div>;
  if (me?.role !== "SUPER_ADMIN") {
    return (
      <div className="p-4">
        <div className="p-3 border-round surface-200 text-700">
          Acceso restringido: el agente de consultas está disponible solo para SUPER_ADMIN por el momento.
        </div>
      </div>
    );
  }

  return (
    <div className="p-card gap-4 p-3">
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-4">
        <h1 className="text-center">Consultas</h1>
        {messages.length > 0 && (
          <Button
            icon="pi pi-plus"
            label="Nueva conversación"
            severity="secondary"
            outlined
            size="small"
            onClick={startNewConversation}
            disabled={loading}
          />
        )}
      </div>

      {/* Conversation area */}
      <div
        className="border-1 surface-border border-round p-3 mb-3 overflow-y-auto"
        style={{ minHeight: "320px", maxHeight: "480px" }}
      >
        {messages.length === 0 && !loading && (
          <div className="flex flex-column align-items-center justify-content-center text-color-secondary" style={{ minHeight: "280px" }}>
            <i className="pi pi-comments mb-3" style={{ fontSize: "2rem" }} />
            <p className="m-0 mb-2 font-medium">Preguntale al agente en lenguaje natural</p>
            <p className="m-0 text-sm">Ej: “Quiero los eventos de hoy del club ub”</p>
            <p className="m-0 text-sm">Podés hacer preguntas de seguimiento: “¿y los de mañana?”</p>
            <p className="m-0 text-sm">
              O pedirle una recomendación: “¿conviene mover el fútbol 5 al viernes?”
            </p>
            <p className="m-0 mt-2 text-sm">
              Y podés corregirlo: “cuando digo comunidad ub me refiero al tenant clubUb”
            </p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex mb-3 ${msg.role === "user" ? "justify-content-end" : "justify-content-start"}`}
          >
            {msg.role === "user" ? (
              <div
                className="border-round p-3 bg-primary text-white"
                style={{ maxWidth: "75%", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {msg.content}
              </div>
            ) : msg.data?.learned ? (
              <LearnedMessage entry={msg.data.learned} />
            ) : msg.data ? (
              <AgentMessage data={msg.data} actor={actor} />
            ) : (
              <div
                className="surface-100 border-round p-3 text-color"
                style={{ maxWidth: "75%", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {msg.content}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-content-start mb-3">
            <div className="surface-100 border-round p-3 flex align-items-center gap-2">
              <ProgressSpinner style={{ width: "20px", height: "20px" }} strokeWidth="4" />
              <span className="text-color-secondary text-sm">El agente está pensando…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="flex gap-2 align-items-end">
        <InputTextarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          autoResize
          placeholder="Escribí tu consulta aquí… (Enter para enviar, Shift+Enter para nueva línea)"
          className="flex-1"
          disabled={loading}
        />
        <Button
          icon="pi pi-send"
          label="Enviar"
          onClick={handleSend}
          disabled={!query.trim() || loading}
          loading={loading}
        />
      </div>
    </div>
  );
}